import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { TrafficSeries } from '@/lib/messaging-data'
import type { Freshness } from '@/lib/system-intelligence'
import { FreshnessNote, PanelEmpty, PanelError, PanelLoading } from './PanelState'
import { ChartTooltip } from './ChartTooltip'

interface SentVsRejectedChartProps {
  series: TrafficSeries
  /** Panel heading — the second series is "rejected" for Twilio, "bounced" for mail. */
  title?: string
  /** Legend label for the red band, matching whatever the title called it. */
  secondaryLabel?: string
  freshness?: Freshness
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
}

/**
 * A round ceiling at or above the busiest hour, so the axis reads in whole
 * steps.
 *
 * The scale used to be a fixed 100, which quietly clipped: a 140-message hour
 * drew a bar taller than the panel. It follows the data now, which is why the
 * ticks are labelled rather than assumed.
 */
function ceiling(peak: number): number {
  if (peak <= 0) return 4
  const magnitude = 10 ** Math.floor(Math.log10(peak))
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    const candidate = step * magnitude
    if (candidate >= peak) return candidate
  }
  return 10 * magnitude
}

/**
 * Messages per hour, sent stacked under rejected.
 *
 * Stacked rather than side-by-side because the question is what share of the
 * hour's traffic failed — two adjacent bars make the reader do that division
 * themselves. Plain CSS heights, not SVG: the bars need to stay crisp at any
 * width and each one carries its own tooltip.
 */
export function SentVsRejectedChart({
  series,
  title = 'Sent vs Rejected',
  secondaryLabel = 'Rejected',
  freshness,
  isLoading,
  error,
  onRetry,
}: SentVsRejectedChartProps) {
  /** Bucket under the cursor; null when the pointer is off the plot. */
  const [hovered, setHovered] = useState<number | null>(null)

  const { granularity, buckets: data } = series
  const max = ceiling(Math.max(...data.map((b) => b.sent + b.rejected), 0))
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => Math.round(f * max))
  // Daily bars are few and each label is distinct, so every one is named. Hourly
  // runs are long enough that labelling all of them would overlap, so roughly a
  // dozen are shown and the rest keep their column unlabelled.
  const stride = granularity === 'day' ? 1 : Math.max(1, Math.ceil(data.length / 12))
  /** Wider bars once a run is short enough that hairlines would look accidental. */
  const gapPct = data.length <= 12 ? 1.2 : 0.4
  const gap = gapPct === 1.2 ? 'gap-[1.2%]' : 'gap-[0.4%]'

  /**
   * Centre of bar `i`, as a percentage of the plot's width.
   *
   * The gaps are percentage-based and there are a lot of them — 47 gaps at 0.4%
   * is nearly a fifth of the plot — so `(i + 0.5) / n` is not the bar's centre
   * and drifts further out with every column. This is the real geometry.
   */
  const barWidthPct = (100 - (data.length - 1) * gapPct) / data.length
  const barCentre = (i: number) => i * (barWidthPct + gapPct) + barWidthPct / 2

  return (
    <section className="border-line bg-surface rounded-lg border border-dashed p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
          {title}
        </h2>
        <div className="flex items-center gap-4">
          <Legend tone="bg-healthy" label="Sent" />
          <Legend tone="bg-critical" label={secondaryLabel} />
        </div>
      </div>

      {isLoading ? (
        <PanelLoading />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} />
      ) : data.length === 0 ? (
        <PanelEmpty label="No hours reported in this window." />
      ) : (
        <>
          <div className="mt-5 flex gap-3">
            {/* Y axis is HTML so it stays put while the plot flexes with the card. */}
            <div className="text-fg-subtle flex h-[240px] flex-col justify-between font-mono text-[11px]">
              {ticks.map((tick, i) => (
                <span key={`${tick}-${i}`}>{tick.toLocaleString()}</span>
              ))}
            </div>

            <div className="relative min-w-0 flex-1">
              <div
                className={cn('border-line flex h-[240px] items-end border-b', gap)}
                onMouseLeave={() => setHovered(null)}
              >
                {data.map((bucket, i) => {
                  const total = bucket.sent + bucket.rejected
                  const active = hovered === i
                  return (
                    <div
                      key={bucket.key}
                      onMouseEnter={() => setHovered(i)}
                      className={cn(
                        // h-full makes the whole column the hover target, so the
                        // empty space above a short bar still reads it — a
                        // zero-traffic hour is otherwise unhoverable, which is
                        // exactly the one you want to check.
                        'flex h-full flex-1 cursor-crosshair flex-col justify-end transition-opacity',
                        // Dim the rest rather than brighten the one: the bars are
                        // already at full colour, so there is no brighter to go.
                        hovered !== null && !active && 'opacity-40',
                      )}
                    >
                      {/* Rejected sits on top of sent, so the red cap grows down
                          into the bar as the hour goes wrong. */}
                      <div
                        className={cn('bg-critical rounded-t-[2px]', active && 'brightness-125')}
                        style={{ height: `${(bucket.rejected / max) * 100}%` }}
                      />
                      <div
                        className={cn(
                          'bg-healthy',
                          bucket.rejected === 0 && 'rounded-t-[2px]',
                          active && 'brightness-125',
                        )}
                        style={{ height: `${(bucket.sent / max) * 100}%` }}
                      />
                      <span className="sr-only">
                        {bucket.caption} — {bucket.sent} sent, {bucket.rejected}{' '}
                        {secondaryLabel.toLowerCase()}, {total} total
                      </span>
                    </div>
                  )
                })}
              </div>

              {hovered !== null && data[hovered] && (
                <ChartTooltip
                  label={data[hovered].caption}
                  rows={[
                    { text: `${data[hovered].sent.toLocaleString()} sent`, tone: 'text-healthy' },
                    {
                      text: `${data[hovered].rejected.toLocaleString()} ${secondaryLabel.toLowerCase()}`,
                      tone: data[hovered].rejected > 0 ? 'text-critical' : 'text-fg-muted',
                    },
                  ]}
                  left={barCentre(hovered)}
                  align={
                    hovered < data.length / 8
                      ? 'start'
                      : hovered > data.length - 1 - data.length / 8
                        ? 'end'
                        : 'center'
                  }
                />
              )}

              {/* Labels are positioned over the plot, not laid out as one flex
                  cell per bucket.
                  
                  As flex children they could not shrink below the width of their
                  own text (`min-width: auto`), so at 48 columns the row demanded
                  48 x 33px inside 1251px and overflowed — the last "14:00" landed
                  500px beyond the card. Absolute placement decouples the label
                  from the column width entirely, and anchoring on `barCentre`
                  keeps each one over the bar it belongs to. */}
              <div className="relative mt-3 h-4">
                {data.map((bucket, i) => {
                  const last = data.length - 1
                  // The final bucket always gets a label, and it is pinned to the
                  // right edge — which drags it back toward its neighbour by half
                  // a label. So any strided label inside one full stride of the
                  // end is dropped; without that the two collide on a narrow
                  // viewport (48h at 1120px had them touching).
                  const strided = i % stride === 0 && last - i >= stride
                  const shown = i === hovered || i === last || strided
                  if (!shown) return null
                  const centre = barCentre(i)
                  return (
                    <span
                      key={bucket.key}
                      className={cn(
                        'absolute top-0 font-mono text-[11px] whitespace-nowrap',
                        i === hovered
                          ? 'text-fg font-bold'
                          : i === data.length - 1
                            ? 'text-info font-bold'
                            : 'text-fg-subtle',
                      )}
                      style={{
                        left: `${centre}%`,
                        // Pin the outermost labels inside the plot rather than
                        // letting them hang past its edges.
                        transform:
                          centre < 4
                            ? 'translateX(0)'
                            : centre > 96
                              ? 'translateX(-100%)'
                              : 'translateX(-50%)',
                      }}
                    >
                      {bucket.label}
                    </span>
                  )
                })}
              </div>
            </div>
          </div>

          <FreshnessNote freshness={freshness} />
        </>
      )}
    </section>
  )
}

function Legend({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="text-fg-muted flex items-center gap-2 text-[13px]">
      <span className={cn('size-3 rounded-sm', tone)} />
      {label}
    </span>
  )
}
